import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LandingPage } from '@xmedical/core';
import { CoreService } from '../../services/core.service';
import { AuthenticationService } from '../../auth/authentication.service';
import { PracticeStore, UserStore } from '../../state';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit {

    private coreService = inject(CoreService);
    private practiceStore = inject(PracticeStore);
    private userStore = inject(UserStore);
    private auth = inject(AuthenticationService);
    private router = inject(Router);

    private destroyed = false;

    readonly window = window;

    constructor() {
      // ngOnInit is async; if the user navigates away mid-load, bail before
      // navigate() yanks them back to the landing route.
      inject(DestroyRef).onDestroy(() => this.destroyed = true);
    }

    // Await the stores via their promise-based ensureLoaded() rather than reacting
    // to loaded() signals. On a practice switch this view sits idle, so a component
    // effect watching the signals is not guaranteed to be flushed when the reload
    // completes; awaiting the load promise is deterministic.
    async ngOnInit(): Promise<void> {
      // Wait for auth before touching practice-scoped stores. On a refresh at '/'
      // the authenticated shell can render before CoreService.loginProcess has run;
      // loading here first would issue API calls and cache under a null user id.
      await this.auth.authReady;
      // authReady also resolves when checkAuth() found the user unauthenticated (a
      // login() redirect is then in flight). Don't load practice-scoped data in
      // that window — it would issue requests/cache under a null user.
      if (!this.auth.isAuthenticated()) {
        return;
      }
      await this.practiceStore.ensureLoaded();
      if (this.coreService.hasPractices()) {
        await this.userStore.ensureLoaded();
      }
      // The user may have navigated away while the loads were in flight; don't
      // force them back to the landing route.
      if (this.destroyed) {
        return;
      }
      this.navigate();
    }

    private navigate() {
      if (this.coreService.hasPractices()) {
        this.router.navigateByUrl(this.landingRoute());
      } else if (this.coreService.hasInvites()) {
        this.router.navigateByUrl('invites');
      } else {
        this.router.navigateByUrl('error');
      }
    }

    private landingRoute(): string {
      const landingPage = this.userStore.settings()?.landingPage ?? LandingPage.Dashboard;
      // The saved landing page may no longer be accessible (module disabled for
      // the practice, or the permission revoked, since it was chosen) — fall back
      // to the dashboard rather than routing to a page the UI otherwise hides.
      if (!this.coreService.canAccessLandingPage(landingPage)) {
        return 'dashboard';
      }
      switch (landingPage) {
        case LandingPage.Calendar:
          return 'calendar';
        case LandingPage.Referrals:
          return 'referrals';
        default:
          return 'dashboard';
      }
    }
}
